/**
 * THE SCENARIO A FOLLOWER IS ANSWERING, WHATEVER KIND OF BLOCK IT CAME FROM.
 *
 * A follower shows its leader's variant above its own question so the teacher can set the answer
 * without scrolling back to the leader and without opening it. That has existed since the start
 * — for TEXT leaders. The box took an `html` string, and a text variant is the only kind that
 * has one, so a block following an image or a sound showed nothing at all above its question:
 * the one piece of information needed to set the answer was the piece that was missing.
 *
 * Reported plainly: "image doesn't appear in a preview format for a linked block ... whereas
 * text gets re-rendered in a connected block to help with answer setup, image does not. sound
 * doesn't either."
 *
 * Dispatching on the LEADER'S TYPE rather than on which fields the variant happens to carry.
 * Sniffing fields reads as the same thing and fails the same way the `html` check did: silently,
 * for whichever shape nobody thought of. A leader type with no case here renders nothing, which
 * is honest, and the compiler will not warn about it — so the list below is the thing to extend
 * when a fifth display block learns to lead.
 */
import {
  probeRoll, resolveDataVizData,
  type DataVizBlockData, type DrivenRow,
} from '@agavi/blocks';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnswerRefScope } from './answer-ref-context';
import { DataVizCanvas } from './dataviz-canvas';
import { htmlHasContent } from './formula-plaintext';
import { WaveformPlayer } from './sound-waveform-player';
import { useIsPhoneLandscape } from './use-viewport';
import { BlockPreviewProvider, PreviewHtml } from './variable-chip-preview';

import type { Block } from '@/types/experience';

import { useTaskVariables } from '@/contexts/task-variables-context';
import { assetsApi, peekAsset, type AssetSummary } from '@/lib/assets-api';

/**
 * Same frame for every kind, because it is the same claim: this is the stimulus, it was authored
 * elsewhere, and it is not part of this block. `ghosted` is the test surface's one step back —
 * there it sits above a question a teacher is answering and must not compete with it.
 */
function Frame({ ghosted, children }: { ghosted?: boolean; children: React.ReactNode }) {
  return (
    /*
     * THE GAP BELOW BELONGS TO THE SCENARIO, not to whatever is hosting it.
     *
     * It was on `AnswerDisplayFrame`, which only four blocks route through — Numerical and
     * Slider render this straight into their own column, so on those the box and the question
     * sat almost touching however much the frame was opened up. A bordered box against bare
     * prose needs more air than the text-to-control pairs below it, and the only place that is
     * true of every surface at once is here.
     *
     * GHOSTED ONLY. On a test surface the box sits above a question being answered and needs
     * the separation. In the EDITOR it sits above a separator line that already divides them,
     * and the same gap there just left a hole.
     */
    /*
     * THE CAP IS A SHARE OF THE SCREEN, not a fixed 160px.
     *
     * 160px is a tenth of a desktop window and nearly half a phone held sideways: measured on a
     * 667x375 viewport, a dataviz stimulus took 43% of the height, above the question it is
     * context FOR. `min()` keeps the desktop number exactly as it was and only bites where the
     * screen is short — landscape lands at 112px, leaving the question the room it needs.
     *
     * CSS rather than a hook, because the thing that matters is the viewport's height and that
     * is what `vh` reads. A media query would have to pick a breakpoint and would miss a short
     * desktop window, which is the same problem with none of the precision.
     */
    <div className={`${ghosted ? 'mb-4 ' : ''}max-h-[min(10rem,30vh)] overflow-y-auto rounded-md border border-ws-border bg-ws-hover px-3 py-2 text-sm text-ws-muted${ghosted ? ' opacity-75' : ''}`}>
      {children}
    </div>
  );
}

/**
 * Resolve a placed asset to its signed URLs, the same two-step the image and sound blocks use:
 * a synchronous peek at the cache first, so a leader already on screen costs no round trip, and
 * a fetch behind it for a cold one.
 */
function useAsset(assetId: string | undefined): AssetSummary | null {
  const [asset, setAsset] = useState<AssetSummary | null>(() => (assetId ? peekAsset(assetId) ?? null : null));
  useEffect(() => {
    if (!assetId) { setAsset(null); return; }
    const cached = peekAsset(assetId);
    if (cached) { setAsset(cached); return; }
    let cancelled = false;
    assetsApi.get(assetId)
      .then(a => { if (!cancelled) setAsset(a); })
      // Silent. This is a borrowed copy of something the LEADER owns, and the leader reports its
      // own load failures where they can be acted on. A second alarm here would be a second
      // report of one problem, on the block that cannot fix it.
      .catch(() => { if (!cancelled) setAsset(null); });
    return () => { cancelled = true; };
  }, [assetId]);
  return asset;
}

function ImageStimulus({ version, ghosted }: { version: Record<string, unknown>; ghosted?: boolean }) {
  const asset = useAsset(version.assetId as string | undefined);
  const caption = (version.caption as string | undefined)?.trim();
  if (!asset) return null;
  return (
    <Frame ghosted={ghosted}>
      {/* Capped hard. The panel is a narrow column and this is context, not the subject —
          a full-height image would push the question it belongs to off the top of the panel,
          which is the problem this placement exists to solve, one section higher. */}
      <img
        src={asset.urls.display}
        alt={(version.altText as string | undefined) ?? ''}
        // Scaled with the frame above, so the image never becomes the one thing that makes the
        // box scroll on a short screen.
        className='mx-auto block max-h-[min(7rem,22vh)] w-auto rounded'
      />
      {caption && <p className='mt-1 text-center text-xs'>{caption}</p>}
    </Frame>
  );
}

function SoundStimulus({ version, ghosted }: { version: Record<string, unknown>; ghosted?: boolean }) {
  const { t } = useTranslation();
  const asset = useAsset(version.assetId as string | undefined);
  if (!asset) return null;
  return (
    <Frame ghosted={ghosted}>
      {/* Playable, not merely named. Which variant is in force is a question about what it
          SOUNDS like, and a filename does not answer it. */}
      <WaveformPlayer src={asset.urls.audio} t={t} height={32} />
    </Frame>
  );
}

function DataVizStimulus({ version, ghosted }: { version: Record<string, unknown>; ghosted?: boolean }) {
  const variables = useTaskVariables();
  // The canvas takes a NUMBER, so unlike the frame above it cannot be given a share of the
  // screen in CSS. Sized to fit the frame's own short-screen cap instead — a 120px chart inside
  // a 112px box is the frame scrolling to show a picture that was meant to be glanceable.
  //
  // 68, not 84: the chart is not the only thing in the box. At 30vh the frame is 112px, its
  // `py-2` takes 16, and the axis-label row under the chart takes about 24 with its gap — so 84
  // still overflowed by 13 and scrolled. Measured at 667x375 rather than reasoned from the cap.
  const short = useIsPhoneLandscape();
  const data = version as unknown as DataVizBlockData;
  if (!data.series?.some(s => s.points.length > 0)) return null;
  // Resolved against the probe, like the block's own idle card: this is a thumbnail of what a
  // variant looks like, so it needs a number and a random one would redraw the curve on every
  // render of the scenario editor.
  return (
    <Frame ghosted={ghosted}>
      <DataVizCanvas data={resolveDataVizData(data, probeRoll(variables))} height={short ? 68 : 120} />
    </Frame>
  );
}


/**
 * THE ANSWER A DRIVEN ROW IS WRITTEN FOR — the same claim the scenario box makes, in the
 * EDITOR only.
 *
 * A follower shows the variant it is answering; a driven block shows the answer its row was
 * authored against, and for the same reason: the question and the answer key are set without
 * scrolling back to another task. Without it the box names the row in stripped text, and an
 * option can be a photograph, a sound, an equation or a chip that resolves per student —
 * flattening those to a label shows the internal name and not the answer at all.
 *
 * NOT ON THE TEST SURFACE, which is the one thing that changed since it was last here. The
 * owner, on seeing it in both: "there's triplication of information here" — the card already
 * carries the answer on the Earlier answers row, where it is also the CONTROL. "make sure it's
 * only in the editor, it doesn't need to be on the testing block." So it is mounted by
 * `DrivenRowFrame` and NOT by `ScenarioStimulus`, whose seventeen mounts are mostly the test
 * surface.
 *
 * Always ghosted, for the same reason: it is borrowed from another task and must not compete
 * with the question being written beneath it.
 */

/**
 * THE WORDS FOR A ROW THAT HAS NO CONTENT, in one place.
 *
 * `other` and `blank` are named by the SURFACE — their names are words in the teacher's
 * language and `packages/blocks` holds none — so every surface showing one needs the key. Two
 * of them do: the ghosted box here and the card's own control, and a card reading "Animal"
 * where the box reads "No answer" is the two-controls-one-pointer disagreement again.
 */
export const DRIVEN_ROW_NAME: Record<'other' | 'blank', string> = {
  other: 'blocks.drivenVersions.rowOther',
  blank: 'blocks.drivenVersions.rowBlank',
};

/** A row with no authored content of its own — named in words, because that is all it is. */
function NamedRow({ kind }: { kind: 'other' | 'blank' }) {
  const { t } = useTranslation();
  return (
    <Frame ghosted>
      {/* Italic and muted, which is how this app already draws a stand-in rather than content —
          the words are the app's, not the teacher's, and must not read as something authored. */}
      <p className='text-center text-sm italic text-ws-muted'>
        {t(DRIVEN_ROW_NAME[kind])}
      </p>
    </Frame>
  );
}

/**
 * AN OPTION'S CHIPS RESOLVE IN THE DRIVER'S TASK, NOT IN THIS ONE.
 *
 * `PreviewHtml` reads whichever `BlockPreviewProvider` is in scope, and a block mounts its own
 * with its OWN rolled values when selected. So this box — which renders an option authored in
 * an EARLIER task — was resolving that option's chips against the follower's roll. Where both
 * tasks happen to define `{n}`, the teacher was shown a different word with nothing saying so;
 * where only the leader defines it, it resolved to nothing.
 *
 * Task variables are keyed BY TASK and roll per task (`EXPERIENCE_ROLL_SCOPE` exists precisely
 * because experience ones must not be), so "the same name" across two tasks is two variables.
 *
 * `benchVariablesFor` answers it for a NAMED task, which is the only reason this is a two-line
 * fix: the editor already composes each task's roll with the experience's.
 */
function DrivenOptionStimulus({ row, driverTaskId }: {
  row: DrivenRow;
  /** Absent for an alias row, whose chips are experience-scoped by construction. */
  driverTaskId?: string;
}) {
  const media = row.media as
    { kind?: string; assetId?: string; altText?: string } | undefined;
  const asset = useAsset(media?.assetId);
  const { t } = useTranslation();
  const scope = useAnswerRefScope();
  /* Strings, because that is what a chip substitutes. `benchVariablesFor` answers in numbers,
     being the bench's own roll. */
  const driverValues = useMemo(
    () => Object.fromEntries(Object.entries((driverTaskId ? scope?.benchVariablesFor?.(driverTaskId) : undefined) ?? {})
      .map(([k, v]) => [k, String(v)])),
    [scope, driverTaskId],
  );

  if (media?.assetId) {
    if (!asset) return null;
    return (
      <Frame ghosted>
        {media.kind === 'audio'
          ? <WaveformPlayer src={asset.urls.audio} t={t} height={32} />
          : (
            <img
              src={asset.urls.display}
              alt={media.altText ?? ''}
              className='mx-auto block max-h-[min(7rem,22vh)] w-auto rounded'
            />
          )}
      </Frame>
    );
  }
  if (!row.html || !htmlHasContent(row.html)) return null;
  return (
    <Frame ghosted>
      <BlockPreviewProvider value={driverValues}>
        <PreviewHtml html={row.html} />
      </BlockPreviewProvider>
    </Frame>
  );
}

/**
 * The row's own content, or its name where it has none.
 *
 * NEVER NULL where there is anything at all to say. The box is what a teacher designs the
 * question and the answer inside, and with nothing naming the row they are writing blind —
 * reported once already as "why is the scenario not shown in anymore when i'm trying to
 * design the question/answer in the editor?".
 */
export function DrivenRowStimulus({ row, driverTaskId }: { row: DrivenRow; driverTaskId?: string }) {
  if (row.kind !== 'option') return <NamedRow kind={row.kind} />;
  if ((row.media as { assetId?: string } | undefined)?.assetId || (row.html && htmlHasContent(row.html))) {
    return <DrivenOptionStimulus row={row} driverTaskId={driverTaskId} />;
  }
  /* An option with neither media nor words still has to say which row this is. */
  if (!row.label) return null;
  return <Frame ghosted><p className='text-center text-sm text-ws-muted'>{row.label}</p></Frame>;
}

export function ScenarioStimulus({ leader, version, ghosted }: {
  /** The block being followed. Undefined when this block follows nothing. */
  leader: Block | undefined;
  /** The variant in force, in that leader's own shape. */
  version: Record<string, unknown> | null | undefined;
  ghosted?: boolean;
}) {
  /*
   * A SCENARIO ONLY, NOW.
   *
   * The driven axis used to borrow this slot to echo the answer its version was written for.
   * That answer has a place of its own on the bar — the Earlier answers row, where it is also
   * the CONTROL — so the echo was a second copy of one fact, and the inert one. The owner on
   * seeing them together: "there's triplication of information here. i don't think we need
   * the ghosted answer on top ... it's already in the earlier answers section."
   *
   * It earned its place for a SCENARIO and still does, for the reason the owner gave: a
   * leader is often visually distant from the question asking about it, so the stimulus is
   * the only thing on screen saying what the question is about. A driver is in another task
   * entirely — too far to echo at all — and the row names its task with a link to go there.
   *
   * `DrivenStimulus` and the three pieces under it are kept rather than deleted: they are the
   * rendering for an option's media and words, which is what a driven entry will want the day
   * the row shows more than text. Nothing calls them today.
   */
  if (!leader || !version) return null;
  switch (leader.type) {
    case 'text': {
      const html = version.html as string | undefined;
      /* Nothing at all for an unwritten variant, rather than an empty bordered box — a frame
         around nothing reads as content that failed to load. `htmlHasContent`, not a truthiness
         check: tiptap leaves `<p></p>` behind, and a variant holding only a formula is not empty. */
      if (!html || !htmlHasContent(html)) return null;
      /* THROUGH `Frame`, like every other kind. This case carried its own copy of the frame's
         classes, which had already drifted — `max-h-32` against the frame's `max-h-40` — and
         meant a TEXT leader, the commonest kind by far, missed anything added to the frame. The
         gap below the stimulus was exactly that. */
      return <Frame ghosted={ghosted}><PreviewHtml html={html} /></Frame>;
    }
    case 'image': return <ImageStimulus version={version} ghosted={ghosted} />;
    case 'sound': return <SoundStimulus version={version} ghosted={ghosted} />;
    case 'dataviz': return <DataVizStimulus version={version} ghosted={ghosted} />;
    default: return null;
  }
}
